import React, { useState, useEffect, useRef, useCallback } from 'react';
import { track } from '@vercel/analytics';
import { FaArrowLeft, FaUpload, FaEdit, FaCheck, FaSpinner, FaTrash, FaQuestion, FaExclamationTriangle } from 'react-icons/fa';
import 'katex/dist/katex.min.css';
import { InlineMath, BlockMath } from 'react-katex';
import { compressImage } from '../../utils/imageCompression';
import logger from '../../utils/logger';
import './MathWorkflow.scss';

const MathWorkflow = ({
  category,
  setCategory,
  setStep,
  handleResetApp,
  API_BASE_URL,
  authToken,
  setResult,
  userData,
  result,
  shortTaskId
}) => {
  const [currentStage, setCurrentStage] = useState('upload'); // 'upload', 'review', 'feedback'
  const [selectedImages, setSelectedImages] = useState([]); // Changed to array for multiple images
  const [imagePreviews, setImagePreviews] = useState([]); // Changed to array for multiple previews
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [ocrResult, setOcrResult] = useState(null);
  const [editedQuestion, setEditedQuestion] = useState([]);  // Change to array of structured objects
  const [editedAnswer, setEditedAnswer] = useState('');  // Will be removed/unused in new design
  const [feedback, setFeedback] = useState('');
  const [error, setError] = useState('');
  const [imageTypes, setImageTypes] = useState([]);  // 'question' or 'answer'
  const [separateQnA, setSeparateQnA] = useState(false);  // false = combined mode, true = separate Q&A mode
  const [showCombinedWarning, setShowCombinedWarning] = useState(false); // For warning modal
  
  // Add state for FAQ popup
  const [showFAQ, setShowFAQ] = useState(false);
  const [expandedQuestions, setExpandedQuestions] = useState({
    calculations: true,
    corrections: false,
    explanations: false,
    notation: false,
    errors: false,
    feedback: false
  });
  
  // Create a ref for the feedback container
  const feedbackContainerRef = useRef(null);
  
  // Add effect to scroll to top when we navigate to the feedback stage
  useEffect(() => {
    if (currentStage === 'feedback') {
      // Scroll to top of page
      window.scrollTo(0, 0);
      
      // Also scroll the feedbackContainer to top if it exists
      if (feedbackContainerRef.current) {
        feedbackContainerRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [currentStage]);
  
  // Helper function to toggle individual FAQ questions
  const toggleQuestion = (questionId) => {
    // Track FAQ question toggling
    track('math_faq_toggle_question', {
      question_id: questionId,
      new_state: !expandedQuestions[questionId] ? 'expanded' : 'collapsed',
      category
    });
    
    setExpandedQuestions(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }));
  };
  
  // Helper function to toggle FAQ visibility
  const toggleFAQ = () => {
    // Track FAQ visibility toggle
    track('math_faq_toggle', {
      new_state: !showFAQ ? 'visible' : 'hidden',
      category
    });
    
    setShowFAQ(!showFAQ);
  };
  
  // Determine if we're in concept or application mode
  const isConceptMode = category === 'MATH_Concepts';
  
  // Check if user is from UK
  const isUKUser = userData && userData.country && (
    userData.country.toLowerCase() === 'united kingdom' ||
    userData.country.toLowerCase() === 'uk' ||
    userData.country.toLowerCase() === 'great britain' ||
    userData.country.toLowerCase() === 'britain'
  );
  
  // Set the display category - show "GCSE Maths" for UK users with MATH_Concepts
  const displayCategory = (isUKUser && isConceptMode) ? 'GCSE Maths' : 
                         (isConceptMode ? 'Concepts and Skills' : 'Contexts and Applications');
  
  // Reset currentStage to 'upload' when authToken changes (user logs in)
  // but only if we don't have a result to display
  useEffect(() => {
    if (!result && authToken) {
      logger.info('Auth token changed and no result exists - resetting MathWorkflow to upload stage');
      setCurrentStage('upload');
    }
  }, [authToken, result]);
  
  // Check for existing feedback from props or localStorage
  useEffect(() => {
    // Check for result passed directly to the component
    if (result) {
      logger.info('Using feedback passed directly to MathWorkflow as prop:', result);
      
      // Check if we have a 'Pending feedback...' placeholder and need to fetch the actual feedback
      if (result === 'Pending feedback...' && shortTaskId) {
        logger.info('Found placeholder feedback, fetching latest from API using task_id:', shortTaskId);
        
        // Fetch the latest feedback
        const fetchLatestFeedback = async () => {
          try {
            // Ensure no double slashes in the URL
            const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
            const response = await fetch(`${baseUrl}/api/math-b/feedback/${shortTaskId}`, {
              headers: {
                'Authorization': `Bearer ${authToken}`
              }
            });
            
            if (!response.ok) {
              throw new Error(`Failed to fetch latest feedback: ${response.status}`);
            }
            
            const data = await response.json();
            
            if (data.status === 'success' && data.feedback) {
              logger.info('Successfully fetched latest feedback');
              logger.info('Raw feedback data:', data.feedback);
              
              // Check if the feedback is still pending
              if (data.feedback.feedback === 'Pending feedback...') {
                logger.info('Feedback is still pending, polling for task status');
                // If still pending, poll for task completion
                pollTaskStatus(shortTaskId);
              } else {
                // Set the feedback from the response - extract just the feedback text
                let feedbackContent = data.feedback.feedback;
                
                // Ensure feedback is a string
                if (typeof feedbackContent !== 'string') {
                  logger.warn('Feedback from API is not a string, converting:', feedbackContent);
                  // Try to extract just the feedback if it's an object with a feedback property
                  if (typeof feedbackContent === 'object' && feedbackContent.feedback) {
                    feedbackContent = feedbackContent.feedback;
                  } else {
                    feedbackContent = String(feedbackContent);
                  }
                }
                
                // If the string contains JSON structure, try to parse it and extract just the feedback
                if (feedbackContent.startsWith('{') && feedbackContent.includes('"feedback":')) {
                  try {
                    const parsedObj = JSON.parse(feedbackContent);
                    if (parsedObj.feedback) {
                      feedbackContent = parsedObj.feedback;
                    }
                  } catch (e) {
                    // If parsing fails, keep the original string
                    logger.warn('Failed to parse feedback JSON:', e);
                  }
                }
                
                // Log feedback content for debugging
                logger.info('Setting feedback content:', feedbackContent.substring(0, 100) + '...');
                
                // Preserve math expressions and formatting
                // Replace escaped math delimiters if needed
                feedbackContent = feedbackContent
                  .replace(/\\\$/g, '$')  // Replace escaped dollar signs
                  .replace(/\\n/g, '\n');  // Replace literal '\n' with actual newlines
                
                setFeedback(feedbackContent);
                setResult(feedbackContent); // Also update the parent component
                setCurrentStage('feedback');
              }
            } else {
              throw new Error('Failed to get feedback data');
            }
          } catch (error) {
            logger.error('Error fetching math feedback:', error);
            setError(`Error loading feedback: ${error.message}`);
          }
        };
        
        fetchLatestFeedback();
      } else if (result !== 'Pending feedback...') {
        // Only set feedback and move to feedback stage if result is not the placeholder
        // and contains actual content
        if (result.trim().length > 0) {
          logger.info('Setting feedback to result value and moving to feedback stage');
          setFeedback(result);
          setCurrentStage('feedback');
        } else {
          logger.warn('Result exists but is empty, staying in upload stage');
        }
      } else {
        logger.warn('Result is a placeholder without shortTaskId, staying in upload stage');
      }
      return;
    }
    
    // Check if we have saved feedback from localStorage
    try {
      const savedEssayData = localStorage.getItem('continueEssay');
      if (savedEssayData) {
        const parsedData = JSON.parse(savedEssayData);
        
        // Check if this is math feedback that should be displayed
        if (parsedData.isMathFeedback && parsedData.result) {
          logger.info('Loading saved math feedback from localStorage:', parsedData);
          
          // Set the feedback state to display the saved feedback
          setFeedback(parsedData.result);
          
          // Store the result in the parent component too if not already set
          if (!result) {
            setResult(parsedData.result);
          }
          
          // Prepare question data for display
          let questionParts = [];
          
          // Check if we have question text
          if (parsedData.question) {
            questionParts.push({
              type: 'context',
              content: parsedData.question
            });
          }
          
          // Check if we have answer text
          if (parsedData.answer) {
            questionParts.push({
              type: 'question',
              label: 'Question',
              questionText: 'Math question',
              handwrittenAnswer: parsedData.answer
            });
          }
          
          // Set edited question state
          setEditedQuestion(questionParts);
          
          // Skip to the feedback stage
          setCurrentStage('feedback');
          
          // Clear localStorage after using it to avoid showing this again on reload
          localStorage.removeItem('continueEssay');
        }
      }
    } catch (error) {
      logger.error('Error loading saved math feedback:', error);
    }
  }, [result, setResult, API_BASE_URL, authToken, shortTaskId]);
  
  // Handle file selection - updated for multiple files with compression
  const handleFileSelect = async (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      
      // Validate each file
      const invalidFiles = newFiles.filter(file => !file.type.match('image.*'));
      if (invalidFiles.length > 0) {
        setError('Please select only image files');
        return;
      }
      
      const oversizedFiles = newFiles.filter(file => file.size > 10 * 1024 * 1024); // 10MB limit
      if (oversizedFiles.length > 0) {
        setError('All files must be less than 10MB');
        return;
      }
      
      setError('');
      
      // Create compressed images and previews for the new files
      const compressedFiles = [];
      const newPreviews = [];
      const newImageTypes = [];
      
      // Set submitting state to show loading indicator
      setIsSubmitting(true);
      
      try {
        // Process each file
        for (let i = 0; i < newFiles.length; i++) {
          const file = newFiles[i];
          
          // Compress the image
          const compressedFile = await compressImage(file);
          compressedFiles.push(compressedFile);
          
          // Create preview
          const reader = new FileReader();
          await new Promise((resolve) => {
            reader.onload = (e) => {
              newPreviews.push(e.target.result);
              resolve();
            };
            reader.readAsDataURL(compressedFile);
          });
          
          // Set default image type (first as 'combined', any others as 'answer')
          newImageTypes.push('combined');
        }
        
        // Update state with compressed files and previews
        setSelectedImages(prev => [...prev, ...compressedFiles]);
        setImagePreviews(prev => [...prev, ...newPreviews]);
        setImageTypes(prev => [...prev, ...newImageTypes]);
        
        // Track image upload
        track('math_image_upload', {
          file_count: newFiles.length,
          category,
          mode: separateQnA ? 'separate' : 'combined'
        });
      } catch (error) {
        logger.error('Error compressing images:', error);
        setError('Error processing images. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };
  
  // Remove an image from the selection
  const handleRemoveImage = (index) => {
    // Track image removal
    track('math_image_remove', {
      image_index: index,
      remaining_images: selectedImages.length - 1,
      category
    });
    
    setSelectedImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setImageTypes(prev => prev.filter((_, i) => i !== index));
  };
  
  // New function to move an image up in the order
  const handleMoveImageUp = (index) => {
    if (index === 0) return; // Already at the top
    
    // Swap with the previous image
    setSelectedImages(prev => {
      const newImages = [...prev];
      [newImages[index-1], newImages[index]] = [newImages[index], newImages[index-1]];
      return newImages;
    });
    
    // Also swap the previews
    setImagePreviews(prev => {
      const newPreviews = [...prev];
      [newPreviews[index-1], newPreviews[index]] = [newPreviews[index], newPreviews[index-1]];
      return newPreviews;
    });
    
    // Also swap the image types
    setImageTypes(prev => {
      const newTypes = [...prev];
      [newTypes[index-1], newTypes[index]] = [newTypes[index], newTypes[index-1]];
      return newTypes;
    });
  };
  
  // New function to move an image down in the order
  const handleMoveImageDown = (index) => {
    if (index >= selectedImages.length - 1) return; // Already at the bottom
    
    // Swap with the next image
    setSelectedImages(prev => {
      const newImages = [...prev];
      [newImages[index], newImages[index+1]] = [newImages[index+1], newImages[index]];
      return newImages;
    });
    
    // Also swap the previews
    setImagePreviews(prev => {
      const newPreviews = [...prev];
      [newPreviews[index], newPreviews[index+1]] = [newPreviews[index+1], newPreviews[index]];
      return newPreviews;
    });
    
    // Also swap the image types
    setImageTypes(prev => {
      const newTypes = [...prev];
      [newTypes[index], newTypes[index+1]] = [newTypes[index+1], newTypes[index]];
      return newTypes;
    });
  };
  
  // Helper function to clean up formatting artifacts
  const cleanFormatting = (text) => {
    if (!text) return '';
    
    // Remove part markers like "--- b ---"
    const cleanedText = text.replace(/---\s*[a-z]\s*---\\n/gi, '');
    
    // Remove extra newlines
    return cleanedText.replace(/\\n\\n/g, '\\n').replace(/\\n/g, '\n');
  };
  
  // Debug function to save content to file
  const saveDebugData = () => {
    // Track debug data download
   
    
    // Process the content with debugging information
    const debugProcessContent = (content) => {
      const debugInfo = {
        original: content,
        processingSteps: [],
        segments: [],
        hasDelimiters: content?.includes('$$') || false,
        delimiterPairs: (content?.match(/\$\$/g) || []).length / 2,
        lineTagsPresent: content?.includes('Line:') || false
      };
      
      if (!content || !content.includes('$$')) {
        debugInfo.processingSteps.push({ step: 'early_return', reason: 'no_delimiters' });
        return debugInfo;
      }
      
      // Track if we're handling a Line: tag
      const isLineTag = content.trim().startsWith('Line:');
      debugInfo.hasLinePrefix = isLineTag;
      
      if (isLineTag) {
        const linePrefix = 'Line: ';
        const lineContent = content.substring(content.indexOf(linePrefix) + linePrefix.length);
        debugInfo.processingSteps.push({ 
          step: 'line_tag_detected', 
          linePrefix: 'Line: ',
          lineContent 
        });
        
        // Process line content for deeper debugging
        const lineDebugInfo = debugProcessContent(lineContent);
        debugInfo.lineContentDebugInfo = lineDebugInfo;
      }
      
      // Simulate the regex pattern processing
      const pattern = /\$\$(.+?)\$\$/gs;
      let lastIndex = 0;
      let match;
      
      while ((match = pattern.exec(content)) !== null) {
        // Track text segment before math
        if (match.index > lastIndex) {
          const textSegment = {
            type: 'text',
            index: debugInfo.segments.length,
            startPos: lastIndex,
            endPos: match.index,
            content: content.slice(lastIndex, match.index)
          };
          debugInfo.segments.push(textSegment);
        }
        
        // Track math segment
        const mathSegment = {
          type: 'math',
          index: debugInfo.segments.length,
          startPos: match.index,
          endPos: pattern.lastIndex,
          delimiters: '$$',
          innerContent: match[1],
          fullContent: match[0]
        };
        debugInfo.segments.push(mathSegment);
        
        // Update last position
        lastIndex = pattern.lastIndex;
      }
      
      // Track any remaining text
      if (lastIndex < content.length) {
        const textSegment = {
          type: 'text',
          index: debugInfo.segments.length,
          startPos: lastIndex,
          endPos: content.length,
          content: content.slice(lastIndex)
        };
        debugInfo.segments.push(textSegment);
      }
      
      return debugInfo;
    };
    
    // Main debug data object
    const debugData = {
      raw_feedback: result,
      formatted_feedback: cleanFormatting(result),
      processing: debugProcessContent(cleanFormatting(result)),
      category,
      step: currentStage,
      timestamp: new Date().toISOString(),
      browser: navigator.userAgent,
      pre_render_state: {
        hasDelimiters: result?.includes('$$') || false,
        lineTagsCount: (result?.match(/Line:/g) || []).length,
        errorTagsCount: (result?.match(/\[ERROR\]/g) || []).length,
        delimiterPairs: (result?.match(/\$\$/g) || []).length / 2,
      }
    };
    
    // Create a file with the debug data
    const blob = new Blob([JSON.stringify(debugData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    
    // Create a link and trigger the download
    const a = document.createElement('a');
    a.href = url;
    a.download = `math-debug-${new Date().getTime()}.json`;
    document.body.appendChild(a);
    a.click();
    
    // Also save the raw feedback as a text file
    const rawTextBlob = new Blob([result || ''], { type: 'text/plain' });
    const rawTextUrl = URL.createObjectURL(rawTextBlob);
    
    // Create a link and trigger the download for raw text
    const rawTextLink = document.createElement('a');
    rawTextLink.href = rawTextUrl;
    rawTextLink.download = `math-raw-feedback-${new Date().getTime()}.txt`;
    document.body.appendChild(rawTextLink);
    rawTextLink.click();
    
    // Clean up
    setTimeout(() => {
      URL.revokeObjectURL(url);
      URL.revokeObjectURL(rawTextUrl);
      document.body.removeChild(a);
      document.body.removeChild(rawTextLink);
    }, 100);
    
    // Also log to console for immediate inspection
    console.log('Debug data saved:', debugData);
    logger.info('Math feedback debug data saved to files (JSON and raw text)');
  };
  
  // Helper function to detect and format math expressions
  const formatMathContent = (text, questionMap = {}) => {
    if (!text) return null;
    
    // Process math expressions marked with $$ delimiters
    const processMathDelimiters = (content) => {
      // If no math delimiters, return the original text
      if (!content.includes('$$')) return content;
      
      // Special handling for Line: tags
      const isLineTag = content.trim().startsWith('Line:');
      if (isLineTag) {
        // If this is a Line: tag, we need to process it specially
        // to avoid splitting the content improperly
        
        // Extract the part after "Line: " to process it
        const linePrefix = 'Line: ';
        const lineContent = content.substring(content.indexOf(linePrefix) + linePrefix.length);
        
        // Create a Line: prefix component
        const lineTag = <strong key="line-tag">Line:</strong>;
        
        // Process the content separately
        const processedContent = processMathContent(lineContent);
        
        // Return a fragment with the Line: prefix and processed content
        return (
          <>
            {lineTag}{' '}
            {processedContent}
          </>
        );
      }
      
      // For regular content (not Line: tags), use a regex-based approach
      return processMathContent(content);
    };
    
    // Helper function to process math content with regex
    const processMathContent = (content) => {
      if (!content.includes('$$')) return content;
      
      // Use regex that supports multi-line LaTeX content with /s flag (dotAll)
      // This captures everything between matched $$ pairs, even across lines
      const segments = [];
      const pattern = /\$\$(.+?)\$\$/gs;
      let lastIndex = 0;
      let match;
      
      while ((match = pattern.exec(content)) !== null) {
        // Add any text before this math block
        if (match.index > lastIndex) {
          segments.push({
            type: 'text',
            content: content.slice(lastIndex, match.index)
          });
        }
        
        // Add the math content (captured inside $$ delimiters)
        segments.push({
          type: 'math',
          content: match[1]
        });
        
        // Update last position
        lastIndex = pattern.lastIndex;
      }
      
      // Add any remaining text after the last math block
      if (lastIndex < content.length) {
        segments.push({
          type: 'text',
          content: content.slice(lastIndex)
        });
      }
      
      // Render each segment appropriately
      return segments.map((segment, index) => {
        if (segment.type === 'math') {
          // Render math content
          try {
            return <InlineMath key={index} math={segment.content} />;
          } catch (e) {
            logger.error("KaTeX error:", e);
            return <span key={index} className="math-error">{segment.content}</span>;
          }
        } else {
          // Render regular text
          return <span key={index}>{segment.content}</span>;
        }
      });
    };

    // Simple function to check if a line contains an error tag
    const hasError = (line) => {
      if (!line) return false;
      return line.includes('[ERROR]');
    };
    
    // Remove the error tag from a line
    const processErrorTag = (line) => {
      if (!line) return '';
      // Just remove the [ERROR] tag
      return line.replace(/\[ERROR\]/g, '').trim();
    };
    
    // Find the number of error tags in the entire feedback for debugging
    const errorTagCount = (text.match(/\[ERROR\]/g) || []).length;
    logger.info(`Total [ERROR] tags in formatMathContent: ${errorTagCount}`);
    
    // Split the feedback into sections based on markdown headers (## Section)
    const sections = text.split(/(?=## )/);
    
    return (
      <div className="formatted-feedback">
        {sections.map((section, sectionIndex) => {
          // Skip empty sections
          if (!section.trim()) return null;
          
          // Count error tags in this section
          const sectionErrorCount = (section.match(/\[ERROR\]/g) || []).length;
          logger.info(`Section ${sectionIndex}: ${sectionErrorCount} error tags`);
          
          // Check if this is a header section
          const lines = section.split('\n');
          const headerMatch = lines[0].match(/^## (.*)/);
          
          if (headerMatch) {
            // Extract the header text
            const headerText = headerMatch[1];
            
            // Try to match this section with a question from the question map
            // Updated regex to properly handle Roman numerals and other non-space part labels
            const partMatch = headerText.match(/part\s+(\S+)/i);
            const questionMatch = headerText.match(/question\s+(\S+)/i);
            const partLabel = partMatch ? partMatch[1].toLowerCase() : 
                             questionMatch ? questionMatch[1].toLowerCase() : null;
            
            // Log for debugging
            logger.debug(`Section header: "${headerText}", Extracted part label: "${partLabel}", Matched question: ${questionMap[partLabel] ? 'found' : 'not found'}`);
            
            // Get question text if available
            const questionText = partLabel ? questionMap[partLabel] : null;
            
            return (
              <div key={sectionIndex} className="feedback-section">
                <h3 className="feedback-section-title">{processMathDelimiters(headerText)}</h3>
                
                {/* Display matched question text if found AND it's not already in the feedback content */}
                {questionText && !headerText.includes(questionText.substring(0, 20)) && (
                  <div className="question-text">
                    <h4>Question:</h4>
                    <div className="question-content">{questionText}</div>
                  </div>
                )}
                
                {(() => {
                  // Use an IIFE to track which lines we've processed as feedback
                  const processedLineIndices = new Set();
                  
                  return lines.map((line, lineIndex) => {
                    // Skip empty lines and lines we've already processed
                    if (!line.trim() || processedLineIndices.has(lineIndex)) return null;
                    
                    // Check if this line is a line-by-line analysis
                    const lineMatch = line.match(/^Line: (.*)/i);
                    
                    if (lineMatch) {
                      // This is a line analysis
                      const studentLine = lineMatch[1].trim();
                      
                      // Collect all feedback lines until next Line: prefix or end of section
                      let feedbackLines = [];
                      let i = lineIndex + 1;
                      let inErrorBlock = false;
                      
                      // Arrays to collect different types of feedback
                      let currentFeedbackGroup = [];
                      let feedbackGroups = [];
                      
                      // Collect all lines
                      while (i < lines.length && !lines[i].match(/^Line: /i)) {
                        const currentLine = lines[i];
                        
                        // Add line to feedbackLines array
                        feedbackLines.push(currentLine);
                        processedLineIndices.add(i);
                        i++;
                      }
                      
                      // Process all feedback lines, including summaries
                      for (let j = 0; j < feedbackLines.length; j++) {
                        const currentLine = feedbackLines[j];
                        
                        // Check for error block start/continue
                        if (hasError(currentLine)) {
                          // If we were not in an error block before, start a new group
                          if (!inErrorBlock && currentFeedbackGroup.length > 0) {
                            feedbackGroups.push({
                              lines: [...currentFeedbackGroup],
                              isError: false
                            });
                            currentFeedbackGroup = [];
                          }
                          
                          // Add this line (without the error tag) to the current group
                          currentFeedbackGroup.push(processErrorTag(currentLine));
                          inErrorBlock = true;
                        } else if (currentLine.trim() === '') {
                          // Handle empty lines - add to current group
                          currentFeedbackGroup.push('');
                        } else {
                          // This is a normal feedback line
                          
                          // If we were in an error block and now we're not, start a new group
                          if (inErrorBlock) {
                            feedbackGroups.push({
                              lines: [...currentFeedbackGroup],
                              isError: true
                            });
                            currentFeedbackGroup = [];
                            inErrorBlock = false;
                          }
                          
                          // Add to the current group
                          currentFeedbackGroup.push(currentLine);
                        }
                      }
                      
                      // Add the last feedback group if any
                      if (currentFeedbackGroup.length > 0) {
                        feedbackGroups.push({
                          lines: [...currentFeedbackGroup],
                          isError: inErrorBlock
                        });
                      }
                      
                      // Render student line content
                      return (
                        <div key={lineIndex} className="line-analysis-container">
                          {/* Student line by itself in its own block */}
                          <div className="line-analysis">
                            <div className="student-line">
                              <strong>Line:</strong> {processMathDelimiters(studentLine)}
                            </div>
                          </div>
                          
                          {/* Render each feedback group as a separate block */}
                          {feedbackGroups
                            .filter(group => {
                              // Filter out empty groups or groups with only whitespace
                              return group.lines.length > 0 && 
                                    !group.lines.every(line => !line.trim());
                            })
                            .map((group, groupIndex) => {
                              
                              const style = group.isError ? {
                                backgroundColor: '#fff5f5',
                                borderLeft: '3px solid #e9aeae',
                                padding: '0.5em',
                                borderRadius: '4px',
                                marginBottom: '0.5em',
                                color: '#000000',
                                fontWeight: '400'
                              } : {
                                backgroundColor: '#f0f7ff',
                                borderLeft: '3px solid #4a8eff',
                                padding: '0.5em',
                                borderRadius: '4px',
                                marginBottom: '0.5em',
                              };
                              
                              return (
                                <div 
                                  key={`group-${groupIndex}`}
                                  className={`feedback-group ${group.isError ? 'has-error' : ''}`}
                                  style={style}
                                >
                                  <div className="feedback-text">
                                    {group.lines.map((feedbackLine, fbIndex) => {
                                      // Empty lines - render as space for formatting
                                      if (!feedbackLine.trim()) {
                                        return <div key={fbIndex} className="feedback-empty-line">&nbsp;</div>;
                                      }
                                      
                                      return (
                                        <div key={fbIndex} className="feedback-line">
                                          {processMathDelimiters(feedbackLine)}
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              );
                            })}
                        </div>
                      );
                    } else {
                      // Regular paragraph with possible math content
                      const hasErrorTag = hasError(line);
                      const processedLine = hasErrorTag ? processErrorTag(line) : line;
                      
                      // Apply direct styling for error paragraphs
                      const errorStyle = hasErrorTag ? {
                        backgroundColor: '#fff5f5',
                        borderLeft: '3px solid #e9aeae',
                        padding: '0.5em',
                        borderRadius: '4px',
                        marginBottom: '0.5em',
                        color: '#000000',
                        fontWeight: '400'
                      } : {};
                      
                      return (
                        <p 
                          key={lineIndex} 
                          className={`feedback-paragraph ${hasErrorTag ? 'has-error' : ''}`}
                          style={errorStyle}
                        >
                          {processMathDelimiters(processedLine)}
                        </p>
                      );
                    }
                  });
                })()}
              </div>
            );
          } else {
            // Plain text section without a header
            return (
              <div key={sectionIndex} className="feedback-text">
                {processMathDelimiters(section)}
              </div>
            );
          }
        })}
      </div>
    );
  };
  
  // First update the toggleImageType function to cycle through three types
  const toggleImageType = (index) => {
    // Only allow toggling if we're in separate Q&A mode
    if (!separateQnA) return;
    
    setImageTypes(prev => {
      const updated = [...prev];
      // In separate mode, only toggle between question and answer
      const newType = updated[index] === 'question' ? 'answer' : 'question';
      
      // Track image type toggle
      track('math_image_type_toggle', {
        image_index: index,
        old_type: updated[index],
        new_type: newType,
        category
      });
      
      updated[index] = newType;
      return updated;
    });
  };
  
  // Update the handleSubmitImage function to show warning when not using separate mode
  const handleSubmitImage = async () => {
    if (selectedImages.length === 0) {
      setError('Please select at least one image');
      return;
    }
    
    // Show warning if not using separate mode
    if (!separateQnA && selectedImages.length > 0) {
      setShowCombinedWarning(true);
      
      // Track showing warning modal
      track('math_combined_mode_warning_shown', {
        image_count: selectedImages.length,
        category
      });
      
      return;
    }
    
    // If already in separate mode or was confirmed, process the submission
    await processImageSubmission();
  };
  
  // Helper function for the actual submission process
  const processImageSubmission = async () => {
    setIsSubmitting(true);
    setError('');
    
    try {
      const formData = new FormData();
      
      // Check if we're in separate Q&A mode
      if (separateQnA) {
        // Get question and answer images
        const questionImages = selectedImages.filter((_, i) => imageTypes[i] === 'question');
        const answerImages = selectedImages.filter((_, i) => imageTypes[i] === 'answer');
        
        // Sanity check - in separate mode, ensure we have at least one question
        if (questionImages.length === 0) {
          setError('Please mark at least one image as a "Question"');
          setIsSubmitting(false);
          return;
        }
        
        // If we have both question and answer images, use separate processing
        if (questionImages.length > 0 && answerImages.length > 0) {
          logger.info(`Processing ${questionImages.length} question images and ${answerImages.length} answer images`);
          
          // Use the first of each type
          formData.append('question_image', questionImages[0]);
          formData.append('answer_image', answerImages[0]);
          formData.append('has_separate_answer', 'true');
        } else {
          // Only question images were marked
          logger.info(`Processing ${selectedImages.length} images as combined question/answer`);
          
          // Append each image with a unique name
          selectedImages.forEach((image, index) => {
            formData.append(`image_${index}`, image);
          });
        }
      } else {
        // In combined mode, treat all images as having both questions and answers
        logger.info(`Processing ${selectedImages.length} images as combined question/answer`);
        
        // Append each image with a unique name
        selectedImages.forEach((image, index) => {
          formData.append(`image_${index}`, image);
        });
      }
      
      formData.append('category', category);
      formData.append('image_count', selectedImages.length);
      
      // Track image submission
      track('math_images_submitted', {
        image_count: selectedImages.length,
        mode: separateQnA ? 'separate' : 'combined',
        question_count: separateQnA ? selectedImages.filter((_, i) => imageTypes[i] === 'question').length : 0,
        answer_count: separateQnA ? selectedImages.filter((_, i) => imageTypes[i] === 'answer').length : 0,
        category
      });
      
      // Determine which API endpoint to use based on category
      const endpoint = 'math-b/process-image';
      
      // Ensure no double slashes in the URL
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/api/${endpoint}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }
      
      const result = await response.json();
      
      // Check if we've received task IDs for async processing
      if (result.status === 'processing' && result.task_ids && result.task_ids.length > 0) {
        // Wait for all tasks to complete
        await pollOcrTasks(result.task_ids);
      } else {
        // Legacy handling - direct response
        setOcrResult(result);
        
        // Extract question and answer from OCR result
        if (result.question_parts && result.question_parts.length > 0) {
          // For multi-part questions, store them as structured data instead of just text
          const questionParts = [];
          
          // Add shared context if present
          if (result.shared_context) {
            questionParts.push({
              type: 'context',
              content: cleanFormatting(result.shared_context)
            });
          }
          
          // Add each question part
          result.question_parts.forEach((part, index) => {
            const partLabel = part.part_label || `Part ${index + 1}`;
            const questionText = cleanFormatting(part.question_text || part.part_text || '');
            // Check for both handwritten_answer and student_answer fields
            const handwrittenAnswer = cleanFormatting(
              part.handwritten_answer || part.student_answer || ''
            );
            
            questionParts.push({
              type: 'question',
              label: partLabel,
              questionText: questionText,
              handwrittenAnswer: handwrittenAnswer,
              pageNumber: part.page_number || null
            });
          });
          
          setEditedQuestion(questionParts);
        } else {
          // Fallback for older format - convert to structured format
          setEditedQuestion([{
            type: 'question',
            label: 'Question 1',
            questionText: cleanFormatting(result.question || ''),
            handwrittenAnswer: cleanFormatting(result.handwritten_solution || '')
          }]);
        }
        
        // Track successful OCR processing
        track('math_ocr_completed', {
          mode: separateQnA ? 'separate' : 'combined',
          question_part_count: result.question_parts?.length || 1,
          category
        });
        
        // Move to the review stage
        setCurrentStage('review');
      }
    } catch (error) {
      logger.error('Error uploading images:', error);
      setError(`Error uploading and processing images: ${error.message}`);
      setIsSubmitting(false);
      
      // Track error
      track('math_ocr_error', {
        error_message: error.message,
        mode: separateQnA ? 'separate' : 'combined',
        image_count: selectedImages.length,
        category
      });
    }
  };
  
  // Function to poll for OCR task completion
  const pollOcrTasks = async (taskIds) => {
    const isContextMath = !isConceptMode;
    let allTasksComplete = false;
    let attempts = 0;
    const maxAttempts = 120; // 4 minutes at 2s intervals
    const interval = 2000; // Poll every 2 seconds
    
    while (!allTasksComplete && attempts < maxAttempts) {
      attempts++;
      
      // Update progress message
      
      // Sleep before polling again
      await new Promise(resolve => setTimeout(resolve, interval));
      
      try {
        // Check status of each task
        let completedTasks = 0;
        let failedTasks = false;
        
        for (const taskId of taskIds) {
          // Ensure no double slashes in the URL
          const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
          const statusResponse = await fetch(`${baseUrl}/api/math-b/task-status/${taskId}`, {
            headers: {
              'Authorization': `Bearer ${authToken}`
            }
          });
          
          if (!statusResponse.ok) {
            throw new Error(`Failed to check task status: ${statusResponse.status}`);
          }
          
          const statusData = await statusResponse.json();
          
          if (statusData.state === 'SUCCESS') {
            completedTasks++;
          } else if (statusData.state === 'FAILURE') {
            failedTasks = true;
            throw new Error(`Task failed: ${statusData.error || 'Unknown error'}`);
          }
        }
        
        // Update progress
        
        // If all tasks are complete, combine their results
        if (completedTasks === taskIds.length) {
          // Ensure no double slashes in the URL
          const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
          const combineResponse = await fetch(`${baseUrl}/api/math-b/combine-results`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${authToken}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              task_ids: taskIds,
              is_context_math: isContextMath,
              category: category
            })
          });
          
          if (!combineResponse.ok) {
            throw new Error(`Failed to combine results: ${combineResponse.status}`);
          }
          
          const combinedResult = await combineResponse.json();
          
          // Set the result and move to the next stage
          setOcrResult(combinedResult);
          
          // Extract question and answer from OCR result
          if (combinedResult.question_parts && combinedResult.question_parts.length > 0) {
            // For multi-part questions, store them as structured data instead of just text
            const questionParts = [];
            
            // Add shared context if present
            if (combinedResult.shared_context) {
              questionParts.push({
                type: 'context',
                content: cleanFormatting(combinedResult.shared_context)
              });
            }
            
            // Add each question part
            combinedResult.question_parts.forEach((part, index) => {
              const partLabel = part.part_label || `Part ${index + 1}`;
              const questionText = cleanFormatting(part.question_text || part.part_text || '');
              // Check for both handwritten_answer and student_answer fields
              const handwrittenAnswer = cleanFormatting(
                part.handwritten_answer || part.student_answer || ''
              );
              
              questionParts.push({
                type: 'question',
                label: partLabel,
                questionText: questionText,
                handwrittenAnswer: handwrittenAnswer,
                pageNumber: part.page_number || null
              });
            });
            
            setEditedQuestion(questionParts);
          } else {
            // Fallback for older format - convert to structured format
            setEditedQuestion([{
              type: 'question',
              label: 'Question 1',
              questionText: cleanFormatting(combinedResult.question || ''),
              handwrittenAnswer: cleanFormatting(combinedResult.handwritten_solution || '')
            }]);
          }
          
          setCurrentStage('review');
          setIsSubmitting(false);
          setError('');
          allTasksComplete = true;
        }
      } catch (error) {
        logger.error('Error checking OCR task status:', error);
        setError(`Error processing images: ${error.message}`);
        setIsSubmitting(false);
        return; // Exit the polling loop on error
      }
    }
    
    // Handle timeout
    if (!allTasksComplete) {
      setError('Image processing timed out. Please try again or use fewer images.');
      setIsSubmitting(false);
    }
  };
  
  // Extract feedback from the result object
  const extractFeedbackFromResponse = (result) => {
    logger.info('Extracting feedback from response');
    
    try {
      // Check for direct feedback content in the result
      if (result.feedback) {
        logger.info('Found direct feedback property in result');
        return result.feedback;
      }
      
      // For OCR results with combined processing
      if (result.result && result.result.question_parts) {
        logger.info('Processing question parts from OCR result');
        const question_parts = result.result.question_parts;
        return formatMathContent(JSON.stringify(question_parts));
      }
      
      // For task status responses that include nested result objects
      if (result.result && result.result.feedback) {
        logger.info('Found feedback in nested result');
        return result.result.feedback;
      }
      
      // For grading task responses
      if (result.result && result.result.status === 'success') {
        if (result.result.feedback) {
          logger.info('Found feedback in grading result');
          return result.result.feedback;
        }
      }
      
      // For combined results response
      if (result.combined_results) {
        logger.info('Processing combined results');
        return result.combined_results;
      }
      
      // Check if there's a feedback property in the nested result structure
      if (result.result && result.result.result && result.result.result.feedback) {
        logger.info('Found feedback in deeply nested result');
        return result.result.result.feedback;
      }
      
      // For raw responses or generic strings
      if (typeof result === 'string') {
        // If the string looks like JSON, try to parse it first
        if (result.startsWith('{') && result.endsWith('}')) {
          try {
            const parsed = JSON.parse(result);
            if (parsed.feedback) {
              return parsed.feedback;
            }
          } catch (e) {
            // Not valid JSON, continue with string handling
          }
        }
        return result;
      }
      
      // For response with direct JSON data
      if (result.response && typeof result.response === 'string') {
        try {
          const parsedResponse = JSON.parse(result.response);
          if (parsedResponse.feedback) {
            return parsedResponse.feedback;
          }
          return result.response;
        } catch (e) {
          // If not valid JSON, return as is
          return result.response;
        }
      }
      
      // If we can't find a valid feedback format, stringify the result for debugging
      logger.warn('Could not extract feedback in a recognized format');
      if (result && typeof result === 'object') {
        // Log available keys for debugging
        logger.info('Available top-level keys:', Object.keys(result));
        if (result.result && typeof result.result === 'object') {
          logger.info('Available keys in result:', Object.keys(result.result));
        }
      }
      
      // Return a user-friendly message with debugging info
      return 'The system could not retrieve the proper feedback format. This is likely a temporary issue. Please try again or contact support if the problem persists.';
    } catch (error) {
      logger.error('Error extracting feedback:', error);
      return 'An error occurred while processing the feedback. Please try again or contact support.';
    }
  };
  
  // Handle editing confirmation
  const handleConfirmEdits = async () => {
    setIsSubmitting(true);
    setError('');
    
    try {
      // Parse the structured question parts back into OCR result format
      const updatedOcrResult = { ...ocrResult };
      
      // Check if we have question parts
      if (updatedOcrResult.question_parts) {
        // Initialize new array for question parts
        const newQuestionParts = [];
        let contextUpdated = false;
        
        // Process each edited part
        editedQuestion.forEach(part => {
          if (part.type === 'context' && !contextUpdated) {
            // Update shared context
            updatedOcrResult.shared_context = part.content;
            contextUpdated = true;
          } else if (part.type === 'question') {
            // Add to question parts
            newQuestionParts.push({
              part_label: part.label,
              question_text: part.questionText,
              handwritten_answer: part.handwrittenAnswer,
              student_answer: part.handwrittenAnswer // Add student_answer field for context_math_ocr.py compatibility
            });
          }
        });
        
        // Replace the question parts
        updatedOcrResult.question_parts = newQuestionParts;
      } else {
        // For older format, create question parts array
        updatedOcrResult.question_parts = [];
        
        editedQuestion.forEach(part => {
          if (part.type === 'question') {
            updatedOcrResult.question_parts.push({
              part_label: part.label,
              question_text: part.questionText,
              handwritten_answer: part.handwrittenAnswer,
              student_answer: part.handwrittenAnswer // Add student_answer field for context_math_ocr.py compatibility
            });
          }
        });
      }
      
      // Track confirmation of edits
      track('math_confirm_edits', {
        question_part_count: updatedOcrResult.question_parts?.length || 0,
        has_context: !!updatedOcrResult.shared_context,
        category
      });
      
      // Submit for processing - updated to use math-b endpoints
      const endpoint = 'math-b/grade';
      
      // Ensure no double slashes in the URL
      const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
      const url = `${baseUrl}/api/${endpoint}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify(updatedOcrResult)
      });
      
      if (!response.ok) {
        throw new Error(`Server responded with status: ${response.status}`);
      }
      
      const responseData = await response.json();
      logger.info('Math grading task submitted:', responseData);
      
      // Store the task_id for polling - this is the DB task ID, not the Celery task ID
      const taskId = responseData.task_id;
      
      if (!taskId) {
        throw new Error('No task ID returned from server');
      }
      
      // Start polling for task completion
      await pollTaskStatus(taskId);
    } catch (error) {
      logger.error('Error processing math feedback:', error);
      setError(`Error generating feedback: ${error.message}`);
      setIsSubmitting(false);
      
      // Track error
      track('math_feedback_error', {
        error_message: error.message,
        category
      });
    }
  };
  
  // Function to poll for task status
  const pollTaskStatus = async (taskId) => {
    const maxAttempts = 90; // Maximum number of polling attempts (3 minutes)
    const interval = 2000; // Poll every 2 seconds
    let attempts = 0;
    let consecutiveErrors = 0; // Track consecutive errors for retry logic
    const maxConsecutiveErrors = 3; // Fail after this many consecutive errors
    
    // Update UI to show we're processing
    setError('');
    setIsSubmitting(true);
    
    const poll = async () => {
      try {
        if (attempts >= maxAttempts) {
          throw new Error('Task processing timed out. The server is taking too long to process your request.');
        }
        
        attempts++;
        
        // Show progress to user
        if (attempts % 10 === 0) {
          setError(`Still processing... (${Math.round(attempts / 30)} minute${attempts > 30 ? 's' : ''})`);
        }
        
        // Ensure no double slashes in the URL
        const baseUrl = API_BASE_URL.endsWith('/') ? API_BASE_URL.slice(0, -1) : API_BASE_URL;
        
        // First, try to get the feedback directly
        if (attempts > 3) { // After a few initial attempts, try the direct feedback endpoint
          try {
            const feedbackResponse = await fetch(`${baseUrl}/api/math-b/feedback/${taskId}`, {
              headers: {
                'Authorization': `Bearer ${authToken}`
              }
            });
            
            if (feedbackResponse.ok) {
              const feedbackData = await feedbackResponse.json();
              
              // Check if the feedback is available and not the placeholder
              if (feedbackData.status === 'success' && 
                  feedbackData.feedback && 
                  feedbackData.feedback.feedback && 
                  feedbackData.feedback.feedback !== 'Pending feedback...') {
                
                logger.info('Feedback retrieved directly:', feedbackData);
                const feedbackText = feedbackData.feedback.feedback;
                setFeedback(feedbackText);
                setResult(feedbackText); // Store in parent component too
                
                setCurrentStage('feedback');
                setIsSubmitting(false);
                setError(''); // Clear any progress messages
                return; // Exit polling as we have the feedback
              }
            }
          } catch (e) {
            logger.warn('Error getting direct feedback (continuing with task status check):', e);
            // Continue with task status check even if direct feedback fails
          }
        }
        
        // If direct feedback isn't available yet, check task status
        const response = await fetch(`${baseUrl}/api/math-b/task-status/${taskId}`, {
          headers: {
            'Authorization': `Bearer ${authToken}`
          }
        });
        
        if (!response.ok) {
          // Handle HTTP error status
          consecutiveErrors++;
          
          // If we've had too many consecutive errors, fail
          if (consecutiveErrors >= maxConsecutiveErrors) {
            throw new Error(`Server responded with status: ${response.status}. Please try again later.`);
          }
          
          // For transient errors, retry after a delay
          logger.warn(`Task status HTTP error (attempt ${attempts}): ${response.status}`);
          setTimeout(poll, interval);
          return;
        }
        
        // Reset consecutive errors counter on successful response
        consecutiveErrors = 0;
        
        const data = await response.json();
        logger.info(`Task ${taskId} status: ${data.state}`);
        
        if (data.state === 'SUCCESS') {
          // Task completed successfully
          const result = data.result;
          
          // Check for errors in the result
          if (!result) {
            throw new Error('Task completed but returned no result data');
          }
          
          if (result.status === 'error') {
            throw new Error(result.error || 'Unknown processing error');
          }
          
          // Extract feedback from result
          const feedbackText = extractFeedbackFromResponse(result);
          setFeedback(feedbackText);
          setResult(feedbackText); // Store in parent component too
          
          setCurrentStage('feedback');
          setIsSubmitting(false);
          setError(''); // Clear any progress messages
        } else if (data.state === 'FAILURE') {
          // Task failed - check for detailed error info
          let errorMessage = 'Task failed';
          
          if (data.error) {
            errorMessage += `: ${data.error}`;
          }
          
          if (data.error_type) {
            logger.error(`Task failed with error type: ${data.error_type}`);
          }
          
          throw new Error(errorMessage);
        } else {
          // Task still running, continue polling
          setTimeout(poll, interval);
        }
      } catch (error) {
        logger.error(`Error polling task ${taskId} status:`, error);
        setError(`Error generating feedback: ${error.message}`);
        setIsSubmitting(false);
      }
    };
    
    // Start polling
    await poll();
  };
  
  // Handle back button
  const handleBack = () => {
    // Track back button press with current stage information
    track('math_back_button', {
      from_stage: currentStage,
      category
    });
    
    if (currentStage === 'review') {
      setCurrentStage('upload');
    } else if (currentStage === 'feedback') {
      setCurrentStage('review');
    } else {
      // Go back to category selection
      setStep(1);
      setCategory(''); // Reset category when going back to step 1
    }
  };
  
  // Update a specific question part
  const handleUpdateQuestionPart = (index, field, value) => {
    setEditedQuestion(prev => {
      const newParts = [...prev];
      newParts[index] = {
        ...newParts[index],
        [field]: value
      };
      return newParts;
    });
  };
  
  // Render the upload stage - updated for multiple images
  const renderUploadStage = () => (
    <div className="math-upload-container">
      <h2>{displayCategory}</h2>
      <p>Upload clear images of your math questions and your solutions.</p>
      
      {/* Add note about diagrams and graphs */}
      <div className="diagrams-note" style={{
        backgroundColor: '#e3f2fd',
        border: '1px solid #90caf9',
        borderRadius: '4px',
        padding: '12px 16px',
        marginBottom: '20px',
        fontSize: '14px'
      }}>
        <h3 style={{ margin: '0 0 8px 0', fontSize: '16px', color: '#1565c0' }}>Diagrams &amp; Graphs:</h3>
        <p style={{ margin: '0 0 8px 0' }}>
          Pulc's Maths feedback tool covers nearly the entire {isUKUser ? 'GCSE' : 'Leaving Cert'} syllabus. However, it currently struggles to interpret graphs and diagrams.
        </p>
        <p style={{ margin: '0' }}>
          If your question includes a diagram or graph, please double-check that all the relevant information is accurately captured during the scan. If the diagram is unclear or incomplete, Pulc may not be able to provide a correct answer.
        </p>
      </div>
      
      <div className="upload-box">
        <div className="upload-options">
          <label className="separate-qna-option" style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
            <input
              type="checkbox"
              checked={separateQnA}
              onChange={() => {
                // When toggling to separate mode, default first image to question, rest to answer
                if (!separateQnA) {
                  setImageTypes(prev => 
                    prev.map((_, index) => index === 0 ? 'question' : 'answer')
                  );
                } else {
                  // When toggling back to combined mode, set all to combined
                  setImageTypes(prev => prev.map(() => 'combined'));
                }
                setSeparateQnA(!separateQnA);
              }}
              style={{ marginRight: '8px' }}
            />
            <div>
              <span style={{ fontWeight: 'bold' }}>Separate Question and Answer pages</span>
              <p style={{ margin: '4px 0 0 0', fontSize: '0.9em', color: '#555' }}>
                Check this only if your questions and answers are on completely separate pages.
                By default, we'll process each image as having both questions and answers.
                Make sure each part is labelled correctly so Pulc can match questions with answers.
              </p>
            </div>
          </label>
        </div>
        
        <input
          type="file"
          id="math-image-upload"
          accept="image/*"
          onChange={handleFileSelect}
          className="file-input"
          multiple // Enable multiple file selection
        />
        <label htmlFor="math-image-upload" className="upload-label">
          <FaUpload /> Select Images
        </label>
        
        {imagePreviews.length > 0 && (
          <>
            <p className="image-order-hint">
              Images will be processed in the order shown below. Use the up/down arrows to reorder them if needed.
              <br/>
              {separateQnA ? (
                <>
                  <strong>Important:</strong> Mark each image as either a "Question" or "Answer" using the toggle button.
                </>
              ) : (
                <>
                  <strong>Note:</strong> All images will be processed as having both questions and answers on them.
                  Check the box above if your questions and answers are on separate pages.
                </>
              )}
            </p>
            <div className="image-previews-container">
              {imagePreviews.map((preview, index) => (
                <div key={index} className="image-preview-item">
                  <div className="image-page-label">
                    Page {index + 1}
                    {separateQnA && (
                      <span 
                        className={`image-type-label ${imageTypes[index]}`}
                        style={{
                          marginLeft: '8px',
                          padding: '2px 6px',
                          borderRadius: '4px',
                          fontSize: '0.8em',
                          backgroundColor: 
                            imageTypes[index] === 'question' ? '#e1f5fe' : '#fff8e1',
                          color: 
                            imageTypes[index] === 'question' ? '#0277bd' : '#ff8f00',
                          fontWeight: 'bold'
                        }}
                      >
                        {imageTypes[index] === 'question' ? 'Question' : 'Answer'}
                      </span>
                    )}
                  </div>
                  <img src={preview} alt={`Preview ${index + 1}`} />
                  <div className="image-controls">
                    <button 
                      className="move-image-btn move-up" 
                      onClick={() => handleMoveImageUp(index)}
                      disabled={index === 0}
                      title="Move Up"
                      style={{ marginRight: '2px' }}
                    >
                      ↑
                    </button>
                    {separateQnA && (
                      <button 
                        className="toggle-type-btn"
                        onClick={() => toggleImageType(index)}
                        title={imageTypes[index] === 'question' ? 'Change to Answer' : 'Change to Question'}
                        style={{
                          padding: '2px 8px',
                          backgroundColor: imageTypes[index] === 'question' ? '#e1f5fe' : '#fff8e1',
                          color: imageTypes[index] === 'question' ? '#0277bd' : '#ff8f00',
                          border: '1px solid',
                          borderColor: imageTypes[index] === 'question' ? '#81d4fa' : '#ffe082',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.9em',
                          fontWeight: 'bold',
                          marginRight: '2px'
                        }}
                      >
                        {imageTypes[index] === 'question' ? 'Q' : 'A'}
                      </button>
                    )}
                    <button 
                      className="remove-image-btn" 
                      onClick={() => handleRemoveImage(index)}
                      title="Remove"
                      style={{ marginRight: '2px' }}
                    >
                      <FaTrash />
                    </button>
                    <button 
                      className="move-image-btn move-down" 
                      onClick={() => handleMoveImageDown(index)}
                      disabled={index === imagePreviews.length - 1}
                      title="Move Down"
                    >
                      ↓
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
        
        {/* Add combined mode warning modal */}
        {showCombinedWarning && (
          <div className="warning-modal-overlay">
            <div className="warning-modal">
              <div className="warning-modal-header">
                <h3><FaExclamationTriangle style={{ color: '#f39c12', marginRight: '10px' }} /> Important Note</h3>
              </div>
              <div className="warning-modal-content">
                <p>
                  You haven't selected the "Separate Question and Answer pages" option.
                </p>
                <p>
                  This means the system will assume that <strong>both questions and answers appear on the same page(s)</strong> in your uploaded images.
                </p>
                <p>
                  If your questions and answers are on separate pages, please cancel and check the option above.
                </p>
              </div>
              <div className="warning-modal-actions">
                <button 
                  onClick={() => {
                    // Track canceling combined mode
                    track('math_combined_mode_canceled', {
                      image_count: selectedImages.length,
                      category
                    });
                    setShowCombinedWarning(false);
                  }} 
                  className="warning-cancel-button"
                >
                  Cancel
                </button>
                <button 
                  onClick={() => {
                    // Track continuing with combined mode
                    track('math_combined_mode_confirmed', {
                      image_count: selectedImages.length,
                      category
                    });
                    setShowCombinedWarning(false);
                    processImageSubmission();
                  }} 
                  className="warning-confirm-button"
                >
                  Continue with Combined Mode
                </button>
              </div>
            </div>
          </div>
        )}
        
        {error && <div className="error-message">{error}</div>}
        
        <div className="action-buttons">
          <button onClick={handleBack} className="back-button">
            <FaArrowLeft /> Back
          </button>
          <button 
            onClick={handleSubmitImage} 
            className="submit-button"
            disabled={selectedImages.length === 0 || isSubmitting}
          >
            {isSubmitting ? <><FaSpinner className="spinner" /> Processing...</> : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
  
  // Render the review stage with improved multi-part question support
  const renderReviewStage = () => (
    <div className="math-review-container">
      <h2>Review OCR Results</h2>
      <p>Please review and correct any errors in the detected questions and your solutions.</p>
      
      {editedQuestion.map((part, index) => (
        <div key={index} className="review-section">
          {part.type === 'context' ? (
            <>
              <h3>Shared Context:</h3>
              <textarea
                value={part.content}
                onChange={(e) => handleUpdateQuestionPart(index, 'content', e.target.value)}
                rows={6}
                className="review-textarea"
              />
            </>
          ) : (
            <>
              <h3>
                {part.label}
                {part.pageNumber && <span className="page-number"> (Page {part.pageNumber})</span>}
              </h3>
              
              <div className="question-part">
                <label>Question Text:</label>
                <textarea
                  value={part.questionText}
                  onChange={(e) => handleUpdateQuestionPart(index, 'questionText', e.target.value)}
                  rows={4}
                  className="review-textarea"
                />
              </div>
              
              <div className="question-part">
                <label>Your Solution:</label>
                <textarea
                  value={part.handwrittenAnswer}
                  onChange={(e) => handleUpdateQuestionPart(index, 'handwrittenAnswer', e.target.value)}
                  rows={6}
                  className="review-textarea"
                />
              </div>
            </>
          )}
          <hr className="part-divider" />
        </div>
      ))}
      
      {error && <div className="error-message">{error}</div>}
      
      <div className="action-buttons">
        <button onClick={handleBack} className="back-button">
          <FaArrowLeft /> Back
        </button>
        <button 
          onClick={handleConfirmEdits} 
          className="submit-button"
          disabled={isSubmitting}
        >
          {isSubmitting ? <><FaSpinner className="spinner" /> Processing...</> : 'Confirm & Get Feedback'}
        </button>
      </div>
    </div>
  );
  
  // Render the feedback stage with proper math rendering and question references
  const renderFeedbackStage = () => {
    // Create a map from question labels to their text to look up during rendering
    const questionMap = {};
    editedQuestion.forEach(part => {
      if (part.type === 'question') {
        questionMap[part.label.toLowerCase().trim()] = part.questionText;
      } else if (part.type === 'context') {
        let content = part.content;
        
        // Only add delimiters if not already present
        if (!content.includes('$$')) {
          // First, replace ** with superscript notation for better LaTeX rendering
          content = content.replace(/(\w+)\*\*(\d+)/g, (match, base, exp) => `${base}^${exp}`);
          
          // Process the entire string to find multiple math expressions
          let remainingContent = content;
          let resultContent = '';
          
          while (remainingContent.length > 0) {
            // Step 1: Find a number or isolated letter (not followed or preceded by another letter)
            const isolatedMatch = remainingContent.match(/(?<![a-zA-Z])([0-9]|[a-zA-Z])(?![a-zA-Z])/);
            
            if (!isolatedMatch) {
              // No more matches, add the remaining content and break
              resultContent += remainingContent;
              break;
            }
            
            const matchChar = isolatedMatch[0];
            const matchIndex = isolatedMatch.index;
            
            // Skip 'a' or 'I' when they appear to be words rather than variables
            if ((matchChar === 'a' || matchChar === 'I') && 
                (matchIndex === 0 || /[\s,.;:!?'"()]/.test(remainingContent[matchIndex-1])) &&
                (matchIndex + 1 >= remainingContent.length || /[\s,.;:!?'"()]/.test(remainingContent[matchIndex+1]))) {
              // Add the content up to and including this 'a' or 'I' and continue
              resultContent += remainingContent.substring(0, matchIndex + 1);
              remainingContent = remainingContent.substring(matchIndex + 1);
              continue;
            }
            
            // Add content before the match
            resultContent += remainingContent.substring(0, matchIndex);
            
            // Add opening $$ before the match
            resultContent += '$$' + matchChar;
            
            // Search for first consecutive letter pair after the match
            const afterMatch = remainingContent.substring(matchIndex + 1);
            const consecutivePair = afterMatch.match(/[a-zA-Z][a-zA-Z]/);
            
            if (consecutivePair) {
              // Found consecutive pair - add content up to but not including the pair
              const pairIndex = consecutivePair.index;
              
              // Check if there's whitespace before the consecutive pair
              let whitespaceBeforePair = '';
              if (pairIndex > 0 && /\s/.test(afterMatch[pairIndex - 1])) {
                // If whitespace exists before the pair, include it before closing tag
                whitespaceBeforePair = afterMatch[pairIndex - 1];
                resultContent += afterMatch.substring(0, pairIndex - 1) + '$$' + whitespaceBeforePair;
              } else {
                // No whitespace before the pair
                resultContent += afterMatch.substring(0, pairIndex) + '$$';
              }
              
              // Update remaining content to start at the consecutive letter pair
              remainingContent = afterMatch.substring(pairIndex);
            } else {
              // No consecutive pair found - close at the end of the content
              resultContent += afterMatch + '$$';
              break;
            }
          }
          
          content = resultContent;
        }
        
        questionMap['context'] = content;
      }
    });
    
    // Ensure feedback is a string and properly formatted
    let feedbackStr = typeof feedback === 'string' ? feedback : 
                     typeof feedback === 'object' ? JSON.stringify(feedback) : String(feedback);
    
    // Clean up any JSON structure that might be in the feedback
    if (feedbackStr.startsWith('{') && feedbackStr.includes('"feedback":')) {
      try {
        // Try to parse and extract just the feedback field
        const parsedFeedback = JSON.parse(feedbackStr);
        if (parsedFeedback.feedback) {
          feedbackStr = parsedFeedback.feedback;
        }
      } catch (e) {
        logger.warn('Failed to parse feedback JSON in renderFeedbackStage', e);
        
        // Fallback: Try to clean up common patterns manually
        // Remove {"feedback": from the beginning
        if (feedbackStr.startsWith('{"feedback":')) {
          const startIndex = feedbackStr.indexOf('{"feedback":') + '{"feedback":'.length;
          const endIndex = feedbackStr.lastIndexOf(',"status":"success"}');
          
          if (endIndex > startIndex) {
            // We found both the start and end patterns
            feedbackStr = feedbackStr.substring(startIndex, endIndex);
            
            // Remove quotes at the beginning and end if present
            if (feedbackStr.startsWith('"') && feedbackStr.endsWith('"')) {
              feedbackStr = feedbackStr.substring(1, feedbackStr.length - 1);
            }
            
            // Unescape internal quotes and other characters
            feedbackStr = feedbackStr
              .replace(/\\"/g, '"')
              .replace(/\\n/g, '\n')
              .replace(/\\\\/g, '\\');
          }
        }
      }
    }
    
    // Prepare feedback for rendering by cleaning up possible escaping issues
    const preparedFeedback = feedbackStr
      .replace(/\\\$/g, '$')     // Fix escaped dollar signs for math expressions
      .replace(/\\n/g, '\n')     // Replace literal '\n' with actual newlines
      .replace(/\\\\/g, '\\');   // Fix double escaped backslashes
    
    // Log key aspects of the feedback for debugging
    logger.info("FEEDBACK FORMAT:", {
      type: typeof feedback,
      length: preparedFeedback.length,
      hasMathDelimiters: preparedFeedback.includes('$$'),
      hasMarkdownHeaders: preparedFeedback.includes('## '),
      firstFewLines: preparedFeedback.split('\n').slice(0, 3)
    });
    
    // Log error tag presence
    if (preparedFeedback) {
      logger.info("FEEDBACK CONTAINS [ERROR] TAGS:", preparedFeedback.includes('[ERROR]'));
      
      // Count error lines
      const errorCount = (preparedFeedback.match(/\[ERROR\]/g) || []).length;
      logger.info(`FOUND ${errorCount} [ERROR] TAGS IN FEEDBACK`);
      
      // Show sample of error lines for debugging
      if (errorCount > 0) {
        const lines = preparedFeedback.split('\n');
        const errorLines = lines.filter(line => line.includes('[ERROR]'));
        logger.info("SAMPLE ERROR LINES:");
        errorLines.slice(0, 3).forEach((line, i) => {
          logger.info(`ERROR ${i+1}: ${line.substring(0, 50)}...`);
        });
      }
    }
    
    return (
      <div className="math-feedback-container" ref={feedbackContainerRef}>
        <h2>Feedback</h2>
        
        <div className="faq-info-container top-info">
          <p>If you have any questions about your result, check out our <button onClick={toggleFAQ} className="inline-faq-link">FAQ</button></p>
        </div>
        
        <div className="feedback-content">
          {/* Show context at the top if it exists */}
          {questionMap['context'] && (
            <div className="context-section">
              <h3>Problem Context</h3>
              <div className="context-content">
                {formatMathContent(questionMap['context'])}
              </div>
            </div>
          )}
          
          <div className="integrated-feedback">
            {formatMathContent(preparedFeedback, questionMap)}
          </div>
        </div>
        
        <div className="faq-info-container">
          <p>If you have any questions about your result, check out our <button onClick={toggleFAQ} className="inline-faq-link">FAQ</button></p>
        </div>
        
        <div className="action-buttons">
          <button onClick={handleBack} className="back-button">
            <FaArrowLeft /> Back to Edit
          </button>
          <button onClick={() => {
            // Track app reset
            track('math_reset_app', {
              from_stage: currentStage,
              category
            });
            handleResetApp();
          }} className="reset-button">
            Start Over
          </button>
        </div>
        
        {/* FAQ Popup */}
        {showFAQ && (
          <div className="faq-modal-overlay">
            <div className="faq-modal">
              <div className="faq-modal-header">
                <h2>FAQ – Math Feedback</h2>
                <button onClick={toggleFAQ} className="faq-close-button">×</button>
              </div>
              <div className="faq-modal-content">
                <p>
                  Here are some answers to common questions about the math feedback system. Our AI assistant is designed 
                  to help analyze your work, but it has some limitations you should be aware of.
                </p>
                
                <div className="faq-question">
                  <button 
                    className="faq-question-header" 
                    onClick={() => toggleQuestion('calculations')}
                  >
                    <h3>Q. The feedback has incorrect calculations</h3>
                    <span className="faq-toggle-icon">
                      {expandedQuestions.calculations ? '−' : '+'}
                    </span>
                  </button>
                  
                  {expandedQuestions.calculations && (
                    <div className="faq-question-content">
                      <p>
                        Sometimes the AI may make calculation errors. While it has an internal calculator, 
                        there are cases where it might attempt to solve complex operations manually and make mistakes.
                      </p>
                      <p>
                        If you notice a calculation error, you can trust your own work if you're confident in your 
                        answer. For verification, consider using a reliable calculator.
                      </p>
                      <p>
                        These calculation issues are most common with complex multi-step problems or when working 
                        with unusual number formats.
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="faq-question">
                  <button 
                    className="faq-question-header" 
                    onClick={() => toggleQuestion('corrections')}
                  >
                    <h3>Q. The feedback says I'm wrong but then corrects itself</h3>
                    <span className="faq-toggle-icon">
                      {expandedQuestions.corrections ? '−' : '+'}
                    </span>
                  </button>
                  
                  {expandedQuestions.corrections && (
                    <div className="faq-question-content">
                      <p>
                        The AI sometimes realizes it made a mistake while analyzing your work. Since it can't erase 
                        what it already wrote (like a human would), it has to acknowledge the error and provide the 
                        correct analysis afterward.
                      </p>
                      <p>
                        In these cases, the final correction is usually the most accurate assessment. You might see 
                        phrases like "Actually, on second look..." or "I made an error in my analysis..." as the AI 
                        attempts to correct its previous statements.
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="faq-question">
                  <button 
                    className="faq-question-header" 
                    onClick={() => toggleQuestion('explanations')}
                  >
                    <h3>Q. The explanation is difficult to follow</h3>
                    <span className="faq-toggle-icon">
                      {expandedQuestions.explanations ? '−' : '+'}
                    </span>
                  </button>
                  
                  {expandedQuestions.explanations && (
                    <div className="faq-question-content">
                      <p>
                        The AI tries to be thorough in its explanations, but this can sometimes lead to dense or 
                        complex feedback, especially for multistep problems.
                      </p>
                      <p>
                        If you find the explanation hard to follow, try focusing on the specific parts where the AI 
                        points out errors or suggests alternative approaches. The line-by-line analysis (if provided) 
                        can also be more digestible than the overall summary.
                      </p>
                      <p>
                        You can always submit your work again to get a potentially clearer explanation, or try 
                        clarifying your handwriting if that might be causing confusion.
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="faq-question">
                  <button 
                    className="faq-question-header" 
                    onClick={() => toggleQuestion('notation')}
                  >
                    <h3>Q. The feedback misunderstood my handwritten notation</h3>
                    <span className="faq-toggle-icon">
                      {expandedQuestions.notation ? '−' : '+'}
                    </span>
                  </button>
                  
                  {expandedQuestions.notation && (
                    <div className="faq-question-content">
                      <p>
                        Mathematical notation can be particularly challenging for the system to interpret, especially 
                        with handwritten work. Symbols like fractions, exponents, and special operators might be 
                        misread.
                      </p>
                      <p>
                        If you notice that the system has misinterpreted your notation, you can edit the OCR results 
                        in the review stage before getting feedback. For future submissions, try to write symbols 
                        distinctly and use standard notation where possible.
                      </p>
                      <p>
                        Common issues include mistaking division signs for addition, missing negative signs, or 
                        incorrectly grouping terms in complex expressions.
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="faq-question">
                  <button 
                    className="faq-question-header" 
                    onClick={() => toggleQuestion('errors')}
                  >
                    <h3>Q. There are errors in the mathematical formatting</h3>
                    <span className="faq-toggle-icon">
                      {expandedQuestions.errors ? '−' : '+'}
                    </span>
                  </button>
                  
                  {expandedQuestions.errors && (
                    <div className="faq-question-content">
                      <p>
                        Sometimes you might see broken math expressions or formatting issues in the feedback. This is 
                        typically due to syntax errors in the LaTeX formatting used to display mathematical notation.
                      </p>
                      <p>
                        These formatting issues don't necessarily indicate problems with the analysis itself, just 
                        with how it's displayed. You can usually still understand the content despite these formatting 
                        issues.
                      </p>
                      <p>
                        We're continuously working to improve the mathematical rendering capabilities of the system.
                      </p>
                    </div>
                  )}
                </div>
                
                <div className="faq-question">
                  <button 
                    className="faq-question-header" 
                    onClick={() => toggleQuestion('feedback')}
                  >
                    <h3>Q. How can I provide feedback about this tool?</h3>
                    <span className="faq-toggle-icon">
                      {expandedQuestions.feedback ? '−' : '+'}
                    </span>
                  </button>
                  
                  {expandedQuestions.feedback && (
                    <div className="faq-question-content">
                      <p>
                        We value your input on how to improve the math feedback system. If you've noticed consistent 
                        issues or have suggestions for improvements, please use the feedback form below.
                      </p>
                      <p>
                        Specific examples are particularly helpful - if you can describe exactly what went wrong and 
                        include details about the type of problem you were solving, it helps us make targeted 
                        improvements.
                      </p>
                    </div>
                  )}
                </div>
                
                {/* Add direct link to feedback form */}
                <div className="feedback-link-container" style={{
                  marginTop: '20px',
                  padding: '15px',
                  backgroundColor: '#f5f5f5',
                  borderRadius: '6px',
                  textAlign: 'center'
                }}>
                  <p style={{ margin: '0 0 10px 0' }}>
                    Have specific feedback about the Maths tool?
                  </p>
                  <a 
                    href="/feedback" 
                    onClick={() => {
                      track('math_feedback_link_clicked', {
                        from_stage: 'feedback',
                        category
                      });
                    }}
                    style={{
                      display: 'inline-block',
                      padding: '8px 16px',
                      backgroundColor: '#4a8eff',
                      color: 'white',
                      borderRadius: '4px',
                      textDecoration: 'none',
                      fontWeight: 'bold'
                    }}
                  >
                    Submit Feedback
                  </a>
                </div>
              </div>
            </div>
          </div>
        )}
        
        {/* Only show debug button in non-production environments */}
        {process.env.NODE_ENV !== 'production' && (
          <div className="debug-section" style={{ marginTop: '20px', borderTop: '1px dashed #ccc', paddingTop: '10px' }}>
            <button 
              onClick={saveDebugData}
              style={{ 
                padding: '8px 12px', 
                background: '#f0f0f0', 
                border: '1px solid #ccc', 
                borderRadius: '4px',
                cursor: 'pointer'
              }}
            >
              Save Debug Data
            </button>
            <span style={{ marginLeft: '10px', fontSize: '12px', color: '#666' }}>
              (Save rendered content to a file for troubleshooting)
            </span>
          </div>
        )}
      </div>
    );
  };
  
  // Add effect to track when we successfully get feedback
  useEffect(() => {
    if (currentStage === 'feedback' && feedback && feedback.length > 0) {
      track('math_feedback_received', {
        feedback_length: feedback.length,
        category
      });
    }
  }, [currentStage, feedback, category]);
  
  // Add effect to track when mode changes
  useEffect(() => {
    if (selectedImages.length > 0) {
      track('math_mode_changed', {
        mode: separateQnA ? 'separate' : 'combined',
        image_count: selectedImages.length,
        category
      });
    }
  }, [separateQnA, category, selectedImages.length]);
  
  // Track when stage changes
  useEffect(() => {
    if (currentStage !== 'upload') {
      track('math_stage_changed', {
        stage: currentStage,
        category
      });
    }
  }, [currentStage, category]);
  
  // Render the appropriate stage
  const renderContent = () => {
    switch (currentStage) {
      case 'upload':
        return renderUploadStage();
      case 'review':
        return renderReviewStage();
      case 'feedback':
        return renderFeedbackStage();
      default:
        return renderUploadStage();
    }
  };
  
  return (
    <div className="math-workflow">
      {renderContent()}
    </div>
  );
};

export default MathWorkflow; 